import type {
  AuthSession,
  ForgotPasswordInput,
  LoginInput,
  RegisterInput,
  ResetPasswordInput,
  SocialAuthProvider,
  UpdateProfileInput,
  User,
} from '@sshop/domain';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

import type { AuthService } from './AuthService';
import { authSessionDto, socialRedirectUrlDto, userDto } from './auth.dto';
import { toAuthSession, toUser } from './auth.mapper';

export function createLaravelAuthService(transport: Transport): AuthService {
  return {
    async login(input: LoginInput, signal): Promise<AuthSession> {
      const res = await transport.request({
        method: 'POST',
        path: '/auth/login',
        body: { email: input.email, password: input.password },
        ...(signal !== undefined ? { signal } : {}),
      });
      return toAuthSession(parseDto(authSessionDto, res.data, 'POST /auth/login'));
    },

    async register(input: RegisterInput, signal): Promise<AuthSession> {
      const res = await transport.request({
        method: 'POST',
        path: '/auth/register',
        body: {
          name: input.name,
          surname: input.surname,
          phone: input.phone,
          email: input.email,
          password: input.password,
          password_repeat: input.passwordRepeat,
          agree: input.agree,
          ...(input.locale !== undefined ? { locale: input.locale } : {}),
        },
        ...(signal !== undefined ? { signal } : {}),
      });
      return toAuthSession(parseDto(authSessionDto, res.data, 'POST /auth/register'));
    },

    async logout(signal): Promise<void> {
      await transport.request({
        method: 'POST',
        path: '/auth/logout',
        ...(signal !== undefined ? { signal } : {}),
      });
    },

    async me(signal): Promise<User> {
      const res = await transport.request({
        method: 'GET',
        path: '/auth/me',
        ...(signal !== undefined ? { signal } : {}),
      });
      return toUser(parseDto(userDto, res.data, 'GET /auth/me'));
    },

    async updateProfile(input: UpdateProfileInput, signal): Promise<User> {
      const body: Record<string, unknown> = {};
      if (input.name !== undefined) body.name = input.name;
      if (input.surname !== undefined) body.surname = input.surname;
      if (input.idNumber !== undefined) body.id_number = input.idNumber;
      if (input.address !== undefined) body.address = input.address;
      if (input.phone !== undefined) body.phone = input.phone;
      if (input.email !== undefined) body.email = input.email;
      if (input.currentPassword !== undefined) body.current_password = input.currentPassword;
      if (input.password !== undefined) body.password = input.password;
      if (input.passwordConfirmation !== undefined)
        body.password_confirmation = input.passwordConfirmation;

      const res = await transport.request({
        method: 'PATCH',
        path: '/auth/me',
        body,
        ...(signal !== undefined ? { signal } : {}),
      });
      return toUser(parseDto(userDto, res.data, 'PATCH /auth/me'));
    },

    async forgotPassword(input: ForgotPasswordInput, signal): Promise<void> {
      await transport.request({
        method: 'POST',
        path: '/auth/forgot-password',
        body: { email: input.email },
        ...(signal !== undefined ? { signal } : {}),
      });
    },

    async resetPassword(input: ResetPasswordInput, signal): Promise<void> {
      await transport.request({
        method: 'POST',
        path: '/auth/reset-password',
        body: {
          token: input.token,
          email: input.email,
          password: input.password,
          password_confirmation: input.passwordConfirmation,
        },
        ...(signal !== undefined ? { signal } : {}),
      });
    },

    async socialRedirectUrl(provider: SocialAuthProvider, signal): Promise<string> {
      const res = await transport.request({
        method: 'GET',
        path: `/auth/social/${encodeURIComponent(provider)}/redirect-url`,
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(
        socialRedirectUrlDto,
        res.data,
        `GET /auth/social/${provider}/redirect-url`,
      );
      return parsed.url;
    },
  };
}
